/**
 * The role views' table: a role a row, sortable, fifty at a time, with its own
 * download. The people tables' twin for roles.
 */
import React, { useMemo, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, FileSpreadsheet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Panel } from './TalentDashboardParts'
import type { RoleCoverage } from './talentRoleData'

export interface RoleColumn {
  key: string
  label: string
  render: (c: RoleCoverage) => ReactNode
  sortValue?: (c: RoleCoverage) => string | number
  className?: string
}

const PAGE = 50

export function RoleTable({
  title,
  note,
  rows,
  columns,
  onExport,
  empty = 'No roles with these filters.',
}: {
  title: string
  note?: ReactNode
  rows: RoleCoverage[]
  columns: RoleColumn[]
  onExport?: () => void
  empty?: string
}) {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null)
  const [limit, setLimit] = useState(PAGE)
  const sorted = useMemo(() => {
    const col = sort ? columns.find((c) => c.key === sort.key) : undefined
    if (!col?.sortValue) return rows
    const value = col.sortValue
    return [...rows].sort((a, b) => {
      const x = value(a)
      const y = value(b)
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * sort!.dir
      return String(x).localeCompare(String(y), 'en', { sensitivity: 'base', numeric: true }) * sort!.dir
    })
  }, [rows, columns, sort])
  const toggle = (key: string) =>
    setSort((s) => (s && s.key === key ? (s.dir === -1 ? { key, dir: 1 } : null) : { key, dir: -1 }))

  return (
    <Panel
      title={title}
      note={note ?? `${rows.length} ${rows.length === 1 ? 'role' : 'roles'}`}
      right={
        onExport ? (
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={onExport} disabled={!rows.length}>
            <FileSpreadsheet className="h-3.5 w-3.5" />
            Download
          </Button>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <p className="text-xs text-gray-500">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left text-[10px] uppercase tracking-wide text-gray-500">
                {columns.map((c) => (
                  <th key={c.key} scope="col" className={cn('px-2 py-1.5 font-semibold', c.className)}>
                    {c.sortValue ? (
                      <button type="button" className="inline-flex items-center gap-1 hover:text-heritage-blue" onClick={() => toggle(c.key)}>
                        {c.label}
                        {sort?.key === c.key ? (
                          sort.dir === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : null}
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, limit).map((row) => (
                <tr key={row.key} className="border-b border-gray-100 align-top">
                  {columns.map((c) => (
                    <td key={c.key} className={cn('px-2 py-1.5 text-gray-700', c.className)}>
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {sorted.length > limit ? (
            <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500">
              Showing {limit} of {sorted.length}.
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setLimit((n) => n + PAGE)}>
                Show {Math.min(PAGE, sorted.length - limit)} more
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setLimit(sorted.length)}>
                Show all
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  )
}

/** "n (x%)": successors against positions, coloured the way the deck bands them. */
export function CoverageCell({ count, positions }: { count: number; positions: number }) {
  const share = positions > 0 ? Math.round((count / positions) * 100) : 0
  return (
    <span
      className={cn(
        'inline-block min-w-[4.5rem] rounded px-1.5 py-0.5 text-center tabular-nums',
        share === 0 ? 'bg-rose-50 text-rose-800' : share < 100 ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800',
      )}
    >
      {count} ({share}%)
    </span>
  )
}
